import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import style from "./AviationDocumentsTab.module.scss";
import { File } from "../../../../../../services";
import DocumentNewIcon from "../../../../../shared/icons/DocumentNewIcon";
import DeleteNewIcon from "../../../../../shared/icons/DeleteNewIcon";
import UnloadIcon from "../../../../../shared/icons/UnloadIcon";
import EyeIcon from "../../../../../shared/icons/EyeIcon";
import CloseIconNew from "../../../../../shared/icons/CloseIconNew";
import Loading from "../../../../../shared/Loading/Loading";
import DocumentPreviewModal from "../../../../../shared/DocumentPreviewModal/DocumentPreviewModal";
import DocumentUploadSubtitle from "../../../../../shared/DocumentUploadSubtitle/DocumentUploadSubtitle";
import {
  canPreviewDocument,
  isImageDocument,
  isPdfDocument,
} from "../../../../../../utils/documentPreviewUtils";

const formatDocumentType = (document) => {
  const mimeMap = {
    "application/pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  };

  return (
    mimeMap[document.contentType] ||
    document.fileName?.split(".").pop()?.toLowerCase() ||
    document.name?.split(".").pop()?.toLowerCase()
  );
};

const AviationDocumentsTab = ({ order, documents = [], refreshDocuments }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state);
  const fileInputRef = useRef(null);
  const cachedFilesRef = useRef({});

  const [loadingDownload, setLoadingDownload] = useState({});
  const [loadingPreview, setLoadingPreview] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    return () => {
      Object.values(cachedFilesRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  const cacheFileUrl = (fileId, url) => {
    cachedFilesRef.current[fileId] = url;
  };

  const resetUploadModal = () => {
    setDocumentName("");
    setUploadFile(null);
    setIsDragging(false);
    setIsUploadModalOpen(false);
  };

  const handleUpload = async () => {
    if (!documentName.trim()) {
      toast.error(t("orderInfo.documentsTab.docNameRequired"));
      return;
    }
    if (!uploadFile) {
      toast.error(t("orderInfo.documentsTab.fileRequired"));
      return;
    }
    if (!order?.id) return;

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("FileName", uploadFile.name);
    formData.append("orderId", order.id);
    formData.append("name", documentName.trim());
    formData.append("Name", documentName.trim());
    formData.append("type", 7);

    try {
      setIsUploading(true);
      await File.addOrderDoc(formData);
      toast.success(t("orderInfo.orderDecisionAviation.uploadFilesSuccess"));
      refreshDocuments && refreshDocuments();
      resetUploadModal();
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfo.documentsTab.uploadError"));
    } finally {
      setIsUploading(false);
    }
  };

  const downloadFile = async (document) => {
    if (!document) return;

    try {
      setLoadingDownload((current) => ({ ...current, [document.id]: true }));
      let url = cachedFilesRef.current[document.fileId];

      if (!url) {
        const response = await File.downloadFileById(document.fileId);
        url = URL.createObjectURL(
          new Blob([response.data], { type: response.data?.type || document.contentType }),
        );
        cacheFileUrl(document.fileId, url);
      }

      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.fileName || document.name || "file";
      link.click();
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfo.listFilesOrder.downloadFileError"));
    } finally {
      setLoadingDownload((current) => ({ ...current, [document.id]: false }));
    }
  };

  const openFile = async (document, title) => {
    if (!canPreviewDocument(document)) return;

    const documentKey = document.id || document.fileId;
    const isPdfFile = isPdfDocument(document);
    const pdfWindow = isPdfFile
      ? window.open("about:blank", "_blank")
      : null;

    if (isPdfFile && !pdfWindow) {
      toast.error(t("orderInfo.listFilesOrder.openFileError"));
      return;
    }

    if (pdfWindow) {
      pdfWindow.opener = null;
    }

    const showFile = (url) => {
      if (isPdfFile) {
        pdfWindow.location.href = url;
        return;
      }

      if (isImageDocument(document)) {
        setPreviewImage({ url, title });
      }
    };

    const cachedUrl = cachedFilesRef.current[document.fileId];
    if (cachedUrl) {
      showFile(cachedUrl);
      return;
    }

    try {
      setLoadingPreview((current) => ({ ...current, [documentKey]: true }));
      const response = await File.downloadFileById(document.fileId);
      const url = URL.createObjectURL(
        new Blob([response.data], {
          type: document.contentType || response.data?.type,
        }),
      );
      cacheFileUrl(document.fileId, url);
      showFile(url);
    } catch (error) {
      console.error(error);
      if (pdfWindow && !pdfWindow.closed) {
        pdfWindow.close();
      }
      toast.error(t("orderInfo.listFilesOrder.openFileError"));
    } finally {
      setLoadingPreview((current) => ({ ...current, [documentKey]: false }));
    }
  };

  const handleDelete = async () => {
    if (!deletingFileId) return;

    try {
      setIsDeleting(true);
      await File.deleteDocumentOrders(deletingFileId);
      toast.success(t("orderInfo.listFilesOrder.deleteSuccess"));
      refreshDocuments && refreshDocuments();
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfo.listFilesOrder.deleteError"));
    } finally {
      setIsDeleting(false);
      setDeletingFileId(null);
    }
  };

  const handleDownloadAll = async () => {
    if (!order?.id) return;

    try {
      setIsDownloadingAll(true);
      const response = await File.downloadZipByAirOrderId(order.id);
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `order_${order.id}_documents.zip`;
      link.click();
      URL.revokeObjectURL(url);
      setIsDownloadModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfo.genericError"));
    } finally {
      setIsDownloadingAll(false);
    }
  };

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <div className={style.header_title}>
          <span className={style.header_label}>{t("orderInfo.documentsTab.allDocuments")}</span>
          <span className={style.header_dot} />
          <span className={style.header_count}>
            {documents.length} {t("orderInfo.documentsTab.filesCount")}
          </span>
        </div>
        <div className={style.header_actions}>
          <button type="button" className={style.upload_btn} onClick={() => setIsUploadModalOpen(true)}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M6.667 13.333 10 10l3.333 3.333M10 10v7.5m6.992-2.175a4.167 4.167 0 0 0-1.992-7.825h-1.05A6.667 6.667 0 0 0 2.5 13.583"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{t("orderInfo.documentsTab.uploadDocument")}</span>
          </button>
          <button type="button" className={style.download_btn} onClick={() => setIsDownloadModalOpen(true)}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="m6.417 9.75 3 3a.833.833 0 0 0 1.166 0l3-3M10 12.5V4.167M5 16.667h10a1.667 1.667 0 0 0 1.667-1.667v-1.667M3.333 13.333V15A1.667 1.667 0 0 0 5 16.667"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{t("orderInfo.documentsTab.downloadAll")}</span>
          </button>
        </div>
      </div>

      <div className={style.list}>
        {!documents.length ? <div className={style.empty}>{t("orderInfo.documentsTab.empty")}</div> : null}
        {documents.map((document) => {
          const documentType = formatDocumentType(document);
          const title =
            document.name ||
            document.fileName ||
            t("orderInfo.listFilesOrder.defaultFileName");
          const canDelete = user?.companyId === document.companyId && order?.status !== "completed";
          const documentKey = document.id || document.fileId;

          return (
            <div key={document.id} className={style.item}>
              <div className={style.item_icon}>
                <DocumentNewIcon />
              </div>
              <div className={style.item_info}>
                <span className={style.item_name}>{title}</span>
                <DocumentUploadSubtitle
                  document={document}
                  companyId={user?.companyId}
                  uploadedText={t("orderInfo.documentsTab.uploaded")}
                  formattedType={documentType}
                  className={style.item_subtitle}
                />
              </div>
              {loadingDownload[document.id] ? (
                <Loading inBlock marginBottom={0} />
              ) : (
                <button
                  type="button"
                  className={style.action_btn}
                  aria-label={t("orderInfoAviation.documents.download")}
                  onClick={() => downloadFile(document)}
                >
                  <UnloadIcon type={2} />
                </button>
              )}
              {loadingPreview[documentKey] ? (
                <Loading inBlock marginBottom={0} />
              ) : canPreviewDocument(document) ? (
                <button
                  type="button"
                  className={style.action_btn}
                  aria-label={`${t("orderInfo.ordersDriverInfo.viewDocsLabel")}: ${title}`}
                  onClick={() => openFile(document, title)}
                >
                  <EyeIcon />
                </button>
              ) : null}
              {canDelete ? (
                <button
                  type="button"
                  className={style.action_btn}
                  aria-label={t("orderInfoAviation.documents.delete")}
                  onClick={() => setDeletingFileId(document.fileId)}
                >
                  <DeleteNewIcon color="#6D7885" />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      <DocumentPreviewModal
        isOpen={Boolean(previewImage)}
        imageUrl={previewImage?.url}
        title={previewImage?.title}
        closeLabel={t("confirmDocs.buttonClose")}
        onClose={() => setPreviewImage(null)}
      />

      {deletingFileId
        ? ReactDOM.createPortal(
            <div className={style.confirm_overlay} data-portal-modal="true">
              <div className={style.confirm_modal} role="dialog">
                <p className={style.confirm_text}>{t("orderInfo.listFilesOrder.confirmDelete")}</p>
                <div className={style.confirm_actions}>
                  <button
                    type="button"
                    className={cn(style.confirm_btn, style.confirm_btn_cancel)}
                    onClick={() => setDeletingFileId(null)}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    type="button"
                    className={cn(style.confirm_btn, style.confirm_btn_ok)}
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? <Loading inBlock marginBottom={0} /> : t("common.confirm")}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {isUploadModalOpen
        ? ReactDOM.createPortal(
            <div className={style.modal_overlay} onClick={resetUploadModal} data-portal-modal="true">
              <div
                className={style.upload_modal}
                role="dialog"
                aria-label={t("orderInfo.documentsTab.modalTitle")}
                onClick={(event) => event.stopPropagation()}
              >
                <div className={style.modal_topbar}>
                  <span className={style.modal_title}>{t("orderInfo.documentsTab.modalTitle")}</span>
                  <button
                    type="button"
                    className={style.modal_close}
                    aria-label={t("common.cancel")}
                    onClick={resetUploadModal}
                    disabled={isUploading}
                  >
                    <CloseIconNew />
                  </button>
                </div>
                <div className={style.modal_section}>
                  <div className={style.modal_section_header}>
                    <span className={style.modal_section_title}>
                      {t("orderInfo.documentsTab.modalNameSection")}
                    </span>
                  </div>
                  <div className={style.modal_input_wrap}>
                    <label className={style.modal_input_label} htmlFor="aviation-document-name">
                      {t("orderInfo.documentsTab.docNameLabel")}
                      <span className={style.modal_required}>*</span>
                    </label>
                    <input
                      id="aviation-document-name"
                      className={style.modal_input}
                      value={documentName}
                      onChange={(event) => setDocumentName(event.target.value)}
                      placeholder={t("orderInfo.documentsTab.docNamePlaceholder")}
                    />
                  </div>
                </div>
                <div className={style.modal_section}>
                  <div className={style.modal_section_header}>
                    <span className={style.modal_section_title}>
                      {t("orderInfo.documentsTab.modalFileSection")}
                    </span>
                  </div>
                  <div
                    className={cn(style.dropzone, {
                      [style.dropzone_active]: isDragging,
                      [style.dropzone_has_file]: !!uploadFile,
                    })}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(event) => {
                      event.preventDefault();
                      setIsDragging(false);
                      setUploadFile(event.dataTransfer.files?.[0] || null);
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className={style.dropzone_icon}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#6d7885" aria-hidden="true">
                        <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4a7.12 7.12 0 0 0-6.65 4.04A6.01 6.01 0 0 0 6 20h13a5 5 0 0 0 .35-9.96ZM14 13v4h-4v-4H7l5-5 5 5h-3Z" />
                      </svg>
                    </div>
                    {uploadFile ? (
                      <span className={style.dropzone_filename}>{uploadFile.name}</span>
                    ) : (
                      <div className={style.dropzone_text}>
                        <span className={style.dropzone_text_main}>
                          {t("orderInfo.documentsTab.dropzoneClick")}
                        </span>
                        <span className={style.dropzone_text_sub}>
                          {t("orderInfo.documentsTab.dropzoneOr")}
                        </span>
                        <span className={style.dropzone_text_hint}>
                          {t("orderInfoExpeditor.documentsTab.fileHint")}
                        </span>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png,.docx"
                      aria-label={t("orderInfoAviation.documents.selectFile")}
                      onChange={(event) => {
                        setUploadFile(event.target.files?.[0] || null);
                        event.target.value = "";
                      }}
                    />
                  </div>
                </div>
                <div className={style.modal_footer}>
                  <button
                    type="button"
                    className={cn(style.modal_btn, style.modal_btn_cancel)}
                    onClick={resetUploadModal}
                    disabled={isUploading}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    type="button"
                    className={cn(style.modal_btn, style.modal_btn_submit)}
                    onClick={handleUpload}
                    disabled={isUploading}
                  >
                    {isUploading ? <Loading inBlock marginBottom={0} /> : t("common.confirm")}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {isDownloadModalOpen
        ? ReactDOM.createPortal(
            <div className={style.action_overlay} data-portal-modal="true">
              <div
                className={style.action_modal}
                role="dialog"
                aria-label={t("orderInfo.orderHistory.downloadAllTitle")}
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  className={style.action_close}
                  aria-label={t("common.cancel")}
                  onClick={() => setIsDownloadModalOpen(false)}
                >
                  <CloseIconNew />
                </button>
                <div className={style.action_body}>
                  <p className={style.action_title}>{t("orderInfo.orderHistory.downloadAllTitle")}</p>
                  <p className={style.action_description}>{t("orderInfo.orderHistory.downloadAllMessage")}</p>
                </div>
                <div className={style.action_footer}>
                  <button
                    type="button"
                    className={cn(style.action_btn_modal, style.action_btn_cancel)}
                    onClick={() => setIsDownloadModalOpen(false)}
                    disabled={isDownloadingAll}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    type="button"
                    className={cn(style.action_btn_modal, style.action_btn_confirm)}
                    onClick={handleDownloadAll}
                    disabled={isDownloadingAll}
                  >
                    {isDownloadingAll ? <Loading inBlock marginBottom={0} /> : t("common.confirm")}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default AviationDocumentsTab;
